'use client';

import { useState, useCallback } from 'react';
import { useTranslation } from 'next-i18next';
import { Button, LinkStyled, TailwindSpinner , ImageBj } from '@components/common';
import { useSwrApi, ApiHeaderInfoOk } from '@hooks/useSwrApi';
import { MY_BESTIE_PRICE_EUR } from '@components/features/modals/buy-my-bestie/constants';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { showBuyMyBestieModal } from '@components/features/modals/buy-my-bestie/BuyMyBestieModal';
import NiceModal from '@ebay/nice-modal-react';
import { PersonalDataModal } from '@components/templates/job/apply/PersonalDataModal';
import { MissingLocationModal } from '@components/templates/job/apply/MissingLocationModal';
import { useSwrResumePersonalData, useSwrResumePreferences } from '@hooks/useSwrApi/endpoints/resume';
import { useSwrUserPhoneNumber } from '@hooks/useSwrApi/endpoints/user/useSwrUserPhoneNumber';
import {
  fetcher, isPhoneNumberInfoOk,
} from '@utils/data-fetching';
import type { ApiUserPhoneInfo } from '@type/v1-api-types/user';
import GeminiVoiceTrigger from '@components/features/voice/GeminiVoiceTrigger';
import { fetchMyBestieVoiceSession } from '@services/voice/voiceSessionApi';
import { MissionChipsRow } from '@components/features/candidate-prompt-bar/MissionChipsRow';

interface MyBestieLandingProps {
  locale: Locale;
  isAuthenticated: boolean;
}

const BENEFITS_KEYS = [
  'personal.agent.benefits.1',
  'personal.agent.benefits.2',
  'personal.agent.benefits.3',
  'personal.agent.benefits.4',
  'personal.agent.benefits.6',
] as const;

export const MyBestieLanding = ({ locale, isAuthenticated }: MyBestieLandingProps) => {
  const { t } = useTranslation('common');
  const [purchaseCompleted, setPurchaseCompleted] = useState(false);

  const { data: headerInfo, mutate: mutateHeaderInfo } = useSwrApi<ApiHeaderInfoOk>({
    url: '/v1/header-info',
    shouldFetch: isAuthenticated,
    locale,
    apiOptions: { revalidateOnMount: true },
  });

  const { data: personalData, mutate: mutatePersonalData } = useSwrResumePersonalData({
    locale,
    shouldFetch: isAuthenticated,
  });

  const { data: phoneData } = useSwrUserPhoneNumber({
    locale,
    shouldFetch: isAuthenticated,
  });

  const { data: preferencesData } = useSwrResumePreferences({
    locale,
    shouldFetch: isAuthenticated,
  });

  const headerInfoReady = !isAuthenticated || !!headerInfo;
  const myBestieEnabled = purchaseCompleted || !!headerInfo?.myBestieEnabled;

  const handlePurchaseSuccess = useCallback(() => {
    setPurchaseCompleted(true);
    void mutateHeaderInfo();
  }, [mutateHeaderInfo]);

  const openBuyModal = useCallback(() => {
    showBuyMyBestieModal(handlePurchaseSuccess);
  }, [handlePurchaseSuccess]);

  const handleActivateClick = useCallback(async () => {
    const hasFirstName = !!personalData?.firstName;
    const hasLastName = !!personalData?.lastName;
    const phoneNumber = phoneData?.data?.number;
    const phoneCountryCode = phoneData?.data?.country?.data?.countryCode;
    const hasPhone = !!phoneNumber && !!phoneCountryCode;

    let phoneConfirmed = false;
    if (hasPhone) {
      const phoneInfo = await fetcher<ApiUserPhoneInfo>(
        '/v1/user-phone/info',
        locale,
        { method: 'POST', payload: { number: `+${phoneCountryCode}${phoneNumber}` }, stringifyPayload: true },
      );
      phoneConfirmed = isPhoneNumberInfoOk(phoneInfo) && phoneInfo.isConfirmed;
    }

    const checkLocationAndBuy = () => {
      if (preferencesData?.resumeAddress) {
        openBuyModal();
      } else {
        void NiceModal.show(MissingLocationModal, {
          locale,
          onNotNeeded: openBuyModal,
          onUpdateSuccess: openBuyModal,
        });
      }
    };

    if (!hasFirstName || !hasLastName || !hasPhone || !phoneConfirmed) {
      void NiceModal.show(PersonalDataModal, {
        locale,
        onUpdateSuccess: () => {
          void mutateHeaderInfo();
          void mutatePersonalData();
          checkLocationAndBuy();
        },
      });
      return;
    }

    checkLocationAndBuy();
  }, [personalData, phoneData, preferencesData, locale, mutateHeaderInfo, mutatePersonalData, openBuyModal]);

  return (
    <div className="flex min-h-full grow flex-col items-center justify-start bg-surface-100 pb-16 pt-6 lg:pb-8 lg:pt-16">
      <div className="flex w-full max-w-3xl flex-col items-center gap-6 px-4 text-center sm:px-6 lg:px-0">
        {/* Demo voice trigger — commented out, may be moved elsewhere
        <div className="flex w-full items-center justify-end gap-3">
          <GeminiVoiceTrigger
            fetchSession={() => fetchMyBestieVoiceSession(locale)}
            firstMessage={t('inbound.greeting.mybestie')}
            className="size-11 shrink-0 rounded-full bg-secondary text-white shadow-md transition hover:bg-secondary/90 active:scale-95"
          />
          <span className="text-base text-ink/75 sm:text-lg">{t('mybestie.try')}</span>
        </div>
        */}

        <h1 className="w-full text-3xl font-bold leading-snug sm:text-4xl sm:leading-snug lg:text-5xl lg:leading-snug xl:text-[56px] xl:leading-snug">
          {(() => {
            const raw = t('personal.agent.landing.newtitle');
            const [before, rest = ''] = raw.split('{{start}}');
            const [highlighted, after = ''] = rest.split('{{end}}');
            return <>{before}<span className="text-secondary">{highlighted}</span>{after}</>;
          })()}
        </h1>

        <ul className="flex w-full max-w-2xl flex-col gap-3 text-left text-base sm:text-lg">
          {BENEFITS_KEYS.map((key) => (
            <li key={key} className="flex items-start gap-3">
              <CheckCircleIcon className="mt-0.5 size-6 shrink-0 text-secondary" />
              <span>{t(key)}</span>
            </li>
          ))}
        </ul>

        <div className="flex w-full max-w-2xl justify-center pb-8 pt-2">
          {!headerInfoReady && (
            <TailwindSpinner className="size-8 text-primary" />
          )}
          {headerInfoReady && myBestieEnabled && (
            <Button
              type="button"
              styling="solid"
              color="promo"
              size="lg"
              rounding="2xl"
              onClick={() => {}}
            >
              {t('personal.agent.already.activated')}
            </Button>
          )}
          {headerInfoReady && !myBestieEnabled && !isAuthenticated && (
            <LinkStyled
              href={`/login?targetPath=${encodeURIComponent(`/${locale}/my-bestie`)}`}
              styling="solid"
              color="promo"
              size="lg"
              rounding="2xl"
            >
              {t('mc.personal.agent.activate').replace('{amount}', MY_BESTIE_PRICE_EUR.toString())}
            </LinkStyled>
          )}
          {headerInfoReady && !myBestieEnabled && isAuthenticated && (
            <Button
              type="button"
              styling="solid"
              color="promo"
              size="lg"
              rounding="2xl"
              onClick={() => { void handleActivateClick(); }}
            >
              {t('mc.personal.agent.activate').replace('{amount}', MY_BESTIE_PRICE_EUR.toString())}
            </Button>
          )}
        </div>
      </div>

      <div className="w-full max-w-5xl overflow-hidden rounded-2xl">
        <div className="flex flex-col lg:flex-row">
          <div className="w-full lg:w-1/2">
            <ImageBj
              src={`images/bestie/my-bestie-chat-${locale === 'ro' ? 'ro' : 'en'}.png`}
              imgFolder="public"
              alt="My Bestie"
              width={1080}
              height={1350}
              className="h-auto w-full"
            />
          </div>
          <div className="flex w-full flex-col justify-center gap-5 bg-surface-200 p-8 lg:w-1/2 lg:p-12">
            <h2 className="text-2xl font-bold leading-snug sm:text-3xl lg:text-4xl">
              {(() => {
                const raw = t('personal.agent.demo.title');
                const [before, rest = ''] = raw.split('{{start}}');
                const [highlighted, after = ''] = rest.split('{{end}}');
                return <>{before}<span className="text-secondary">{highlighted}</span>{after}</>;
              })()}
            </h2>
            <p className="text-base text-ink/75 sm:text-lg">
              {t('personal.agent.demo.description')}
            </p>
            <div className="flex items-center gap-3">
              <GeminiVoiceTrigger
                fetchSession={() => fetchMyBestieVoiceSession(locale)}
                firstMessage={t('inbound.greeting.mybestie')}
                className="size-11 shrink-0 rounded-full bg-secondary text-white shadow-md transition hover:bg-secondary/90 active:scale-95"
              />
              <span className="text-base text-ink/75 sm:text-lg">{t('personal.agent.demo.test')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Mission chips — none selected, /my-bestie matches no mission. mt-auto pins to bottom. */}
      <MissionChipsRow locale={locale} mode="search" isAuthenticated={isAuthenticated} forceUnselected className="mt-auto px-3 pt-12" />
    </div>
  );
};
